import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useClickOutside } from '../../hooks';
import { IconSpinner } from '../../sharetribe/components';

import {
  fetchOwnListings,
  checkListingAvailability,
} from './ListingSelector.duck';
import ListingItem from './ListingItem/ListingItem';
import css from './ListingSelector.module.css';

const ListingSelector = (props) => {
  const {
    className,
    bookingStart,
    bookingEnd,
    listingId,
    onSelect,
    labelText,
    placeholderText,
    availableText,
    unavailableText,
  } = props;
  const dispatch = useDispatch();
  const dropdownRef = useRef();
  const [listings, setListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(listingId || '');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadListings = async () => {
      setLoading(true);
      try {
        const ownListings = await dispatch(fetchOwnListings());

        if (bookingStart && bookingEnd) {
          const availabilityChecks = await Promise.all(
            ownListings.map(async (listing) => {
              const isAvailable = await dispatch(
                checkListingAvailability(listing.id, bookingStart, bookingEnd)
              );
              return { ...listing, isAvailable };
            })
          );
          setListings(availabilityChecks);
        } else {
          setListings(
            ownListings.map((listing) => ({ ...listing, isAvailable: true }))
          );
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, [bookingStart, bookingEnd, dispatch]);

  useEffect(() => {
    if (listingId) {
      setSelectedListingId(listingId);
    }
  }, [listingId, listings]);

  useEffect(() => {
    setOpen(false);
  }, [selectedListingId]);

  useClickOutside(dropdownRef, () => setOpen(false));

  const handleSelect = (id) => {
    setSelectedListingId(id);
    onSelect(id);
    setOpen(false);
  };

  const selectedListing = listings.find((l) => l.id.uuid === selectedListingId);

  const classes = classNames(css.root, className);
  const selectorBoxClasses = classNames(css.selectorBox, {
    [css.selectedItem]: !!selectedListing,
  });

  return (
    <div className={classes} ref={dropdownRef}>
      <div className={css.dropdownContainer}>
        <label>{labelText}</label>
        {loading ? (
          <IconSpinner />
        ) : (
          <>
            <div
              className={selectorBoxClasses}
              onClick={() => setOpen((prev) => !prev)}
            >
              {selectedListing ? (
                <ListingItem
                  className={css.listingItem}
                  listing={selectedListing}
                  availableText={availableText}
                  unavailableText={unavailableText}
                />
              ) : (
                <span className={css.placeholder}>{placeholderText}</span>
              )}
            </div>

            {open && (
              <div className={css.listingDropdown}>
                {listings.map((listing) => (
                  <ListingItem
                    key={listing.id.uuid}
                    className={css.listingItem}
                    onClick={() =>
                      listing.isAvailable && handleSelect(listing.id.uuid)
                    }
                    listing={listing}
                    availableText={availableText}
                    unavailableText={unavailableText}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ListingSelector;
